import React, { useId } from 'react';

// Ícone (SVG) que se preenche de BAIXO pra CIMA, proporcional a uma
// porcentagem — mesma técnica de duas camadas (silhueta clara embaixo +
// cópia recortada por cima) usada em BusCapacityIcon.jsx pro ônibus da tela
// de Relatórios, só que genérico: recebe o path de qualquer ícone (viewBox
// 0 0 24 24) em vez de vir com um ônibus fixo, pra dar pra reaproveitar em
// "Comparativo" com ícone de aluno, professor, turma etc. Preenchimento de
// baixo pra cima (não da esquerda pra direita como o ônibus) porque aqui é
// um ícone genérico parado, não um veículo — "nível enchendo" é a leitura
// mais natural pra esse tipo de forma.
//
// Os paths usados nessa tela vêm do Material Design Icons (Pictogrammers,
// licença Apache 2.0): account-group, account-tie, google-classroom,
// chart-line — https://pictogrammers.com/library/mdi/
const idSvgSeguro = (id) => id.replace(/[^a-zA-Z0-9]/g, '');

function StatFillIcon({ path, percentage = 0, colorLight, colorDark, size = 'w-14 h-14' }) {
  const clipId = idSvgSeguro(useId());
  const gradVazioId = idSvgSeguro(useId());
  const gradCheioId = idSvgSeguro(useId());
  const pct = Number.isFinite(percentage) ? Math.min(100, Math.max(0, percentage)) : 0;
  const alturaPreenchida = (pct / 100) * 24;

  return (
    <svg viewBox="0 0 24 24" className={size}>
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y={24 - alturaPreenchida} width="24" height={alturaPreenchida} />
        </clipPath>
        <linearGradient id={gradVazioId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#E5E7EB" />
          <stop offset="1" stopColor="#D1D5DB" />
        </linearGradient>
        <linearGradient id={gradCheioId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={colorLight} />
          <stop offset="1" stopColor={colorDark} />
        </linearGradient>
      </defs>
      <path d={path} fill={`url(#${gradVazioId})`} />
      <g clipPath={`url(#${clipId})`}>
        <path d={path} fill={`url(#${gradCheioId})`} />
      </g>
    </svg>
  );
}

export default StatFillIcon;

// Paths prontos (24x24) pra não duplicar essas strings gigantes em cada tela
// que usar o componente.
export const STAT_ICON_PATHS = {
  alunos:
    'M12 5.5A3.5 3.5 0 0 1 15.5 9a3.5 3.5 0 0 1-3.5 3.5A3.5 3.5 0 0 1 8.5 9A3.5 3.5 0 0 1 12 5.5M5 8c.56 0 1.08.15 1.53.42c-.15 1.43.27 2.85 1.13 3.96C7.16 13.34 6.16 14 5 14a3 3 0 0 1-3-3a3 3 0 0 1 3-3m14 0a3 3 0 0 1 3 3a3 3 0 0 1-3 3c-1.16 0-2.16-.66-2.66-1.62a5.54 5.54 0 0 0 1.13-3.96c.45-.27.97-.42 1.53-.42M5.5 18.25c0-2.07 2.91-3.75 6.5-3.75s6.5 1.68 6.5 3.75V20h-13zM0 20v-1.5c0-1.39 1.89-2.56 4.45-2.9c-.59.68-.95 1.62-.95 2.65V20zm24 0h-3.5v-1.75c0-1.03-.36-1.97-.95-2.65c2.56.34 4.45 1.51 4.45 2.9z',
  filaEspera:
    'M10.63 14.1a7 7 0 0 1 9.27-3.47a7 7 0 0 1 3.47 9.27A6.98 6.98 0 0 1 17 24c-2.7 0-5.17-1.56-6.33-4H1v-2c.06-1.14.84-2.07 2.34-2.82S6.72 14.04 9 14c.57 0 1.11.05 1.63.1M9 4c1.12.03 2.06.42 2.81 1.17S12.93 6.86 12.93 8s-.37 2.08-1.12 2.83s-1.69 1.12-2.81 1.12s-2.06-.37-2.81-1.12S5.07 9.14 5.07 8s.37-2.08 1.12-2.83S7.88 4.03 9 4m8 18a5 5 0 0 0 5-5a5 5 0 0 0-5-5a5 5 0 0 0-5 5a5 5 0 0 0 5 5m-1-8h1.5v2.82l2.44 1.41l-.75 1.3L16 17.69z',
  professores:
    'M12 3c2.21 0 4 1.79 4 4s-1.79 4-4 4s-4-1.79-4-4s1.79-4 4-4m4 10.54c0 1.06-.28 3.53-2.19 6.29L13 15l.94-1.88c-.62-.07-1.27-.12-1.94-.12s-1.32.05-1.94.12L11 15l-.81 4.83C8.28 17.07 8 14.6 8 13.54c-2.39.7-4 1.96-4 3.46v4h16v-4c0-1.5-1.6-2.76-4-3.46',
  turmas:
    'M23 2H1a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h22a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1m-1 18h-2v-1h-5v1H2V4h20zM10.29 9.71A1.71 1.71 0 0 1 12 8c.95 0 1.71.77 1.71 1.71c0 .95-.76 1.72-1.71 1.72s-1.71-.77-1.71-1.72m-4.58 1.58c0-.71.58-1.29 1.29-1.29a1.29 1.29 0 0 1 1.29 1.29c0 .71-.58 1.28-1.29 1.28S5.71 12 5.71 11.29m10 0A1.29 1.29 0 0 1 17 10a1.29 1.29 0 0 1 1.29 1.29c0 .71-.58 1.28-1.29 1.28s-1.29-.57-1.29-1.28M20 15.14V16H4v-.86c0-.94 1.55-1.71 3-1.71c.55 0 1.11.11 1.6.3c.75-.69 2.1-1.16 3.4-1.16s2.65.47 3.4 1.16c.49-.19 1.05-.3 1.6-.3c1.45 0 3 .77 3 1.71',
  frequencia: 'm16 11.78l4.24-7.33l1.73 1l-5.23 9.05l-6.51-3.75L5.46 19H22v2H2V3h2v14.54L9.5 8z',
};
